import { EventBus } from '@pp/toolkit';

import type { BaseCoreGameCommunicationProcessor } from '../../controllers/gameCommunication/processors/BaseCoreGameCommunicationProcessor';
import { MessageName } from '../../controllers/gameCommunication/types';
import { RestartController } from '../../controllers/restartController/restartController';
import { Logger } from '../../logger/Logger';
import { Socket, SOCKET_CONNECTION_STATUS, SocketConstructor } from '../../services/webSocket/types';
import { NativeWebSocket, WebSocketService } from '../../services/webSocket/WebSocketService';
import type { CoreStoreType } from '../../store/CoreStore';

import { CoreCredentialsStrategy } from './credentials/GameCredentialsStrategy';
import type { SocketCredentialsStrategy, CredentialParams } from './credentials/types';
import { GameCommunicationStrategy } from './GameCommunicationStrategy';
import { GAME_STATUS } from './types';
import type {
  GameCommunicationMessageType,
  GameCommunicationTimeout,
  IGameCommunicationConnectionObserver,
  IGameCommunicationSender,
  IGameCommunicationProcessorFactory,
} from './types';

type CommunicationEvents = {
  serverSwitch: () => void;
  earlySend: (sender: IGameCommunicationSender<unknown>, event: CustomEvent<unknown>) => void;
};

class CommunicationEventBus extends EventBus<CommunicationEvents> {
  public dispatchEvent<Event extends keyof CommunicationEvents>(
    event: Event,
    ...args: Parameters<CommunicationEvents[Event]>
  ): void {
    super.dispatchEvent(event, ...args);
  }
}

const isProduction = (): boolean => process.env.NODE_ENV === 'production';

export class GameCommunicationService<
  SocketAdapter extends Socket = NativeWebSocket,
  SocketAdapterConstructor extends SocketConstructor<SocketAdapter> = SocketConstructor<SocketAdapter>,
> extends WebSocketService<GameCommunicationMessageType, SocketAdapter, SocketAdapterConstructor> {
  private store?: CoreStoreType;
  private processors: Record<string, BaseCoreGameCommunicationProcessor> = {};
  private senders: Record<string, IGameCommunicationSender<unknown>> = {};
  private isGamePaused = false;
  private isInitialized = false;
  private connectionObserver: IGameCommunicationConnectionObserver | undefined;
  private initialServerMessageDelay: number = 0;
  private serverMessageDelay: number = 0;

  private processorsQueue: GameCommunicationTimeout[] = [];
  private currentGame: number | null = null;
  private credentialStrategy: SocketCredentialsStrategy = new CoreCredentialsStrategy();

  constructor(
    SocketConstructor: SocketAdapterConstructor,
    useProxyConnection: boolean = false,
    public comEventBus: CommunicationEventBus = new CommunicationEventBus(),
    public communicationStrategy: GameCommunicationStrategy = new GameCommunicationStrategy()
  ) {
    super(SocketConstructor, {
      keepAlive: {
        connectionValidationInterval: 10000,
        reconnectionInterval: 5000,
        reconnectLimit: 5,
      },
      useProxyConnection,
    });

    RestartController.addListener('game-communication', () => {
      if (!this.isInitialized) {
        return;
      }
      this.restartCommunication();
    });

    this.addEventListener('connectionStatusChange', (status) => {
      if (this.store) this.connectionObserver?.update(status, this.store);
      if (status === SOCKET_CONNECTION_STATUS.RECONNECTING) {
        this.processorsQueue.forEach(({ timeout }) => clearTimeout(timeout));
      }
    });

    this.addEventListener('processedMessage', (message) => {
      this.processParsedMessage(message);
    });

    this.addEventListener('error', () => {
      Logger.logFailure('gameCommunicationError', {
        place: 'general',
        additional: 'error',
      });
    });

    this.setStrategy(this.communicationStrategy);
  }

  public setCredentialStrategy(strategy?: SocketCredentialsStrategy): void {
    if (strategy) {
      this.credentialStrategy = strategy;
    }
  }

  public setCredentials = (params: CredentialParams): void => {
    const oldUrl = this.url;

    const context = {
      baseUrl: '',
      searchParams: new URLSearchParams(),
    };

    this.credentialStrategy.setCredentials(params, context);

    this.baseUrl = context.baseUrl;
    this.searchParams = context.searchParams;

    if (this.isAlive) {
      if (this.url !== oldUrl) {
        this.addEventListener('close', () => {
          this.initSocket();
        });
        const acknowledgeReconnection = (): void => {
          this.comEventBus.dispatchEvent('serverSwitch');
          this.removeEventListener('open', acknowledgeReconnection);
        };
        this.addEventListener('open', acknowledgeReconnection);
        this.killCommunication();
      }
    }
  };

  public setServerMessageDelay(delay: number): void {
    this.serverMessageDelay = delay;
    this.initialServerMessageDelay = delay;
  }

  getMessageDelay(delayCorrection?: number): number {
    delayCorrection ??= 0;
    if (this.isInitialized) {
      return this.serverMessageDelay + delayCorrection;
    } else {
      return 0;
    }
  }

  public setStore(store: CoreStoreType): void {
    this.store = store;
  }

  public stopStart(isGamePaused: boolean): void {
    if (this.isGamePaused !== isGamePaused) {
      this.isGamePaused = isGamePaused;

      if (isGamePaused) {
        this.killCommunication();
      } else {
        this.initSocket(true);
      }
    }
  }

  init(): void {
    if (this.isAlive) return;
    this.initSocket();
  }

  private initSocket(reconnect = false): void {
    if (reconnect) {
      this.serverMessageDelay = 0;
    } else {
      this.serverMessageDelay = this.initialServerMessageDelay;
    }

    if (this.isAlive) return;

    performance.mark('websocketConnectionStart');

    this.openSocket();

    // Everything after the first connection is reconnection.
    this.searchParams.set('reconnect', 'true');
  }

  public setInitialized(): void {
    this.isInitialized = true;
    const websocketConnectionTime = performance.measure('websocketConnectionTime', 'websocketConnectionStart');
    if (websocketConnectionTime) {
      Logger.logDuration('communicationController', {
        duration: Math.round(websocketConnectionTime.duration),
      });
    }
  }

  public setProcessor(processorFactory: IGameCommunicationProcessorFactory, store: CoreStoreType): void {
    const processor = new processorFactory(store);
    this.processors[processor.messageName] = processor;
  }

  public setSender(sender: IGameCommunicationSender<unknown>): void {
    if (!this.senders[sender.name]) {
      this.senders[sender.name] = sender;
      window.addEventListener(`GameCommunicationSender-${sender.name}`, ((event: CustomEvent<unknown>) => {
        if (this.store && this.socket) {
          if (this.isAlive) {
            sender.send(event.detail, this.store, this.send.bind(this));
          } else {
            this.comEventBus.dispatchEvent('earlySend', sender, event);
          }
        }
      }) as EventListener);
    }
  }

  public setConnectionObserver(observer: IGameCommunicationConnectionObserver): void {
    this.connectionObserver = observer;
  }

  private connectedProcedure(): void {
    this.serverMessageDelay = this.initialServerMessageDelay;
  }

  private setCurrentGame(gameId: number): void {
    if (this.currentGame === gameId) return;
    if (this.currentGame === null) {
      this.currentGame = gameId;
      return;
    }
    this.processorsQueue.forEach(({ timeout, process, queuedGameId }) => {
      if (queuedGameId === String(gameId)) {
        clearTimeout(timeout);
        process();
      }
    });
    this.processorsQueue = [];
    this.currentGame = gameId;
    if (this.store) {
      this.connectionObserver?.update(GAME_STATUS.NEW_ROUND, this.store);
    }
  }

  processParsedMessage(message: GameCommunicationMessageType): void {
    if (!message || !message.messageName) {
      return;
    }

    if (message.messageName === MessageName.Game) {
      this.setCurrentGame(message.id);
    }

    const messageProcessor = this.processors[message.messageName];

    if (!(messageProcessor && this.store && this.socket)) {
      return;
    }

    const dynamicDelay = this.getMessageDelay(messageProcessor?.delayCorrection);

    message.beforeInit = !this.isInitialized;
    message.meta = message.meta || {};
    message.seq = messageProcessor?.seq ?? message.seq;
    message.meta.delay = messageProcessor?.delay ?? Math.max(dynamicDelay, 0);

    const process = this.processFn.bind(this, message, messageProcessor);
    this.processorsQueue.push({
      messageName: message.messageName,
      seq: message.seq,
      queuedGameId: this.store.getState().gameState.gameId,
      timeout: window.setTimeout(process, message.meta.delay, () => {
        this.processorsQueue.splice(
          this.processorsQueue.findIndex(({ seq }) => seq === message.seq),
          1
        );
      }),
      process,
    });
  }

  private processFn(
    message: GameCommunicationMessageType,
    messageProcessor: BaseCoreGameCommunicationProcessor,
    callback?: () => void
  ): void {
    if (messageProcessor && this.store && this.socket) {
      // As Never is added due to compatibility with the old code
      messageProcessor.process(message, this.store as never, this.send.bind(this) as never, {
        connectionStatus: this.connectionStatus,
        connectionProcedure: this.connectedProcedure.bind(this),
      });
    }
    if (callback) {
      callback();
    }
  }
}

export const gameCommunicationService = new GameCommunicationService(NativeWebSocket, !isProduction());
